/**
 * @CODE:SPEC-ACC-002-FILTERS-001
 * AccommodationFilters Component (Trail-based)
 *
 * Trail-centric filters for accommodation search
 * Displays:
 * - Selected trail indicator
 * - Course selector (based on selected trail)
 * - Distance from course filter
 * - Accommodation type selector
 * - Price range inputs
 */

import { memo, useCallback, useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { MapPin, X } from "lucide-react";
import type { AccommodationFilters, Course } from "@/features/accommodation/types";
import { TRAIL_NAMES } from "@/constants/trails";

interface AccommodationFiltersProps {
  filters: AccommodationFilters;
  /** Selected trail ID */
  selectedTrailId?: string;
  /** Courses for the selected trail */
  courses: Course[];
  types: Array<{ id: number; name: string }>;
  onFiltersChange: (filters: Partial<AccommodationFilters>) => void;
  /** Callback to change trail (scrolls back to trail selector) */
  onChangeTrail?: () => void;
  isLoading?: boolean;
}

/** Distance filter options (in km) */
const DISTANCE_OPTIONS = [
  { value: "1", label: "도보 가능 (1km 이내)" },
  { value: "3", label: "가까움 (3km 이내)" },
  { value: "5", label: "보통 (5km 이내)" },
  { value: "10", label: "전체 (10km 이내)" },
  { value: "20", label: "넓게 (20km 이내)" },
];

const AccommodationFilters = memo(
  ({
    filters,
    selectedTrailId,
    courses,
    types,
    onFiltersChange,
    onChangeTrail,
    isLoading = false,
  }: AccommodationFiltersProps) => {
    const handleCourseChange = useCallback(
      (value: string) => {
        onFiltersChange({
          course_id: value === "all" ? undefined : value,
        });
      },
      [onFiltersChange],
    );

    const handleDistanceChange = useCallback(
      (value: string) => {
        onFiltersChange({
          distance_from_course_km: value ? Number(value) : undefined,
        });
      },
      [onFiltersChange],
    );

    const handleTypeChange = useCallback(
      (value: string) => {
        const selectedType = types.find((t) => t.name === value);
        onFiltersChange({
          accommodation_type: value === "all" ? undefined : value,
          accommodation_type_id: value === "all" ? undefined : selectedType?.id,
        });
      },
      [onFiltersChange, types],
    );

    const handlePriceFromChange = useCallback(
      (value: string) => {
        onFiltersChange({
          priceFrom: value ? Number(value) : undefined,
        });
      },
      [onFiltersChange],
    );

    const handlePriceToChange = useCallback(
      (value: string) => {
        onFiltersChange({
          priceTo: value ? Number(value) : undefined,
        });
      },
      [onFiltersChange],
    );

    const handlePickupChange = useCallback(
      (checked: boolean | "indeterminate") => {
        onFiltersChange({
          has_pickup: checked === true ? true : undefined,
        });
      },
      [onFiltersChange],
    );

    const handleReset = useCallback(() => {
      onFiltersChange({
        course_id: undefined,
        distance_from_course_km: undefined,
        accommodation_type: undefined,
        accommodation_type_id: undefined,
        priceFrom: undefined,
        priceTo: undefined,
        has_pickup: undefined,
      });
    }, [onFiltersChange]);

    // Get selected trail name
    const selectedTrailName = useMemo(() => {
      if (!selectedTrailId) return null;
      return (
        TRAIL_NAMES[selectedTrailId as keyof typeof TRAIL_NAMES] || "선택된 둘레길"
      );
    }, [selectedTrailId]);

    // Check if any filters are active
    const hasActiveFilters = !!(
      filters.course_id ||
      filters.distance_from_course_km ||
      filters.accommodation_type ||
      filters.priceFrom ||
      filters.priceTo ||
      filters.has_pickup
    );

    return (
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col gap-4">
            {/* Trail Indicator & Reset Button */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              {selectedTrailId && (
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#F39800]" />
                    <span className="text-sm font-medium text-gray-700">선택한 둘레길:</span>
                  </div>
                  <Badge variant="secondary" className="text-sm">
                    {selectedTrailName}
                  </Badge>
                  {onChangeTrail && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onChangeTrail}
                      className="text-xs"
                    >
                      변경하기
                    </Button>
                  )}
                </div>
              )}

              {/* Reset Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                disabled={isLoading || !hasActiveFilters}
                className="ml-auto"
              >
                <X className="w-4 h-4 mr-1" />
                필터 초기화
              </Button>
            </div>

            {/* Main Filter Row */}
            <div className="flex flex-wrap gap-2 items-end">
              {/* Course Select */}
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  코스 선택
                </label>
                <Select
                  value={filters.course_id || "all"}
                  onValueChange={handleCourseChange}
                  disabled={isLoading || !selectedTrailId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="전체 코스" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">전체 코스</SelectItem>
                    {courses.map((course) => (
                      <SelectItem key={course.course_id} value={course.course_id}>
                        {course.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Distance Filter */}
              <div className="flex-1 min-w-[180px]">
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  코스로부터 거리
                </label>
                <Select
                  value={filters.distance_from_course_km?.toString() || "10"}
                  onValueChange={handleDistanceChange}
                  disabled={isLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="10km 이내" />
                  </SelectTrigger>
                  <SelectContent>
                    {DISTANCE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Type Select */}
              <div className="flex-1 min-w-[150px]">
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  숙소 유형
                </label>
                <Select
                  value={filters.accommodation_type || "all"}
                  onValueChange={handleTypeChange}
                  disabled={isLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="전체" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">전체</SelectItem>
                    {types.map((type) => (
                      <SelectItem key={type.id} value={type.name}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Pickup Service Filter */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="has-pickup"
                  checked={filters.has_pickup === true}
                  onCheckedChange={handlePickupChange}
                  disabled={isLoading}
                />
                <label
                  htmlFor="has-pickup"
                  className="text-sm font-medium text-gray-700 cursor-pointer"
                >
                  픽업 서비스
                </label>
              </div>
            </div>

            {/* Price Range Row */}
            <div className="flex gap-2 items-end">
              <div className="flex-1 max-w-[200px]">
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  최소 가격
                </label>
                <Input
                  type="number"
                  placeholder="₩0"
                  value={filters.priceFrom || ""}
                  onChange={(e) => handlePriceFromChange(e.target.value)}
                  disabled={isLoading}
                  min={0}
                  step={10000}
                />
              </div>
              <span className="text-gray-500 pb-2">~</span>
              <div className="flex-1 max-w-[200px]">
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  최대 가격
                </label>
                <Input
                  type="number"
                  placeholder="₩999999"
                  value={filters.priceTo || ""}
                  onChange={(e) => handlePriceToChange(e.target.value)}
                  disabled={isLoading}
                  min={0}
                  step={10000}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

AccommodationFilters.displayName = "AccommodationFilters";

export default AccommodationFilters;
